import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, computed, DestroyRef, ElementRef, EventEmitter, inject, Injector, input, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgLetDirective, SafeHtmlPipe, takeWhileAlive } from '@core/frontend-shared/utils';
import { combineLatest, map, of, switchMap, timer } from 'rxjs';
import { UiButtonComponent } from '../button';
import { UiHelpButtonComponent } from '../help-button';
import { UiIconsModule } from '../icons';
import { UiInputGroupAddonDirective, UiInputGroupComponent } from '../input-group';
import { defaultFormControlStatus } from '../input-ref';
import * as i0 from "@angular/core";
import * as i1 from "@angular/common";
import * as i2 from "../icons/icon.component";
const _c0 = ["*", [["ui-help-button"]]];
const _c1 = ["*", "ui-help-button"];
const _c2 = (a0, a1, a2) => ({
  "placeholder-label": a0,
  "placeholder-hide": a1,
  "placeholder-small": a2
});
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "label", 7);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0._labelValue());
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 12);
    i0.ɵɵelement(1, "ui-icon", 14);
    i0.ɵɵelementEnd();
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 15);
    i0.ɵɵlistener("click", function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Conditional_3_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r2);
      const ctx_r0 = i0.ɵɵnextContext(4);
      return i0.ɵɵresetView(ctx_r0.onClickReset());
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(4);
    i0.ɵɵclassMap(ctx_r0.resetButtonClass());
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "ui-input-group", 9);
    i0.ɵɵtemplate(1, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_ng_container_1_Template, 1, 0, "ng-container", 11);
    i0.ɵɵconditionalCreate(2, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Conditional_2_Template, 2, 0, "span", 12);
    i0.ɵɵconditionalCreate(3, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Conditional_3_Template, 1, 2, "button", 13);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const state_r3 = i0.ɵɵnextContext(2).$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    const inputTmpl_r4 = i0.ɵɵreference(2);
    i0.ɵɵproperty("stretchInputs", true);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", inputTmpl_r4);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!state_r3.status.enabled ? 2 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.showResetButton() ? 3 : -1);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_4_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_4_ng_container_0_Template, 1, 0, "ng-container", 11);
  }
  if (rf & 2) {
    i0.ɵɵnextContext(3);
    const inputTmpl_r4 = i0.ɵɵreference(2);
    i0.ɵɵproperty("ngTemplateOutlet", inputTmpl_r4);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_5_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 16)(1, "span", 17);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const state_r3 = i0.ɵɵnextContext(3).$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", state_r3.errors[0]);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_5_Conditional_0_Template, 3, 1, "div", 16);
  }
  if (rf & 2) {
    const state_r3 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵconditional(state_r3.errors.length && state_r3.status.touched ? 0 : -1);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 10);
    i0.ɵɵpipe(1, "safeHtml");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("innerHTML", i0.ɵɵpipeBind1(1, 1, ctx_r0.description()), i0.ɵɵsanitizeHtml);
  }
}
function UiFieldWrapperComponent_ng_template_0_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵconditionalCreate(1, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_1_Template, 2, 1, "label", 7);
    i0.ɵɵelementStart(2, "div", 8);
    i0.ɵɵconditionalCreate(3, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_3_Template, 4, 4, "ui-input-group", 9)(4, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_4_Template, 1, 1, "ng-container");
    i0.ɵɵelementEnd();
    i0.ɵɵconditionalCreate(5, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_5_Template, 1, 1);
    i0.ɵɵconditionalCreate(6, UiFieldWrapperComponent_ng_template_0_ng_template_0_Conditional_6_Template, 2, 3, "div", 10);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const state_r3 = i0.ɵɵnextContext().$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction3(5, _c2, ctx_r0._usePlaceholder(), ctx_r0.hidePlaceholder(), ctx_r0._usePlaceholder() && (ctx_r0.hasValue() || state_r3.status.focused)));
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0._usePlaceholder() ? 1 : -1);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r0.hasAddonButtons() ? 3 : 4);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(state_r3.errors ? 5 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.description() ? 6 : -1);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_2_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "label", 19);
    i0.ɵɵtext(1);
    i0.ɵɵtemplate(2, UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_2_ng_container_2_Template, 1, 0, "ng-container", 11);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    const helpButton_r5 = i0.ɵɵreference(4);
    i0.ɵɵproperty("title", ctx_r0._labelValue());
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0._labelValue(), "\u00A0 ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", helpButton_r5);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_2_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_5_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 21);
    i0.ɵɵtemplate(1, UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_5_ng_container_1_Template, 1, 0, "ng-container", 11);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵnextContext(3);
    const helpButton_r5 = i0.ɵɵreference(4);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", helpButton_r5);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4)(1, "div", 18);
    i0.ɵɵconditionalCreate(2, UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_2_Template, 3, 3, "label", 19);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 20);
    i0.ɵɵtemplate(4, UiFieldWrapperComponent_ng_template_0_Conditional_2_ng_container_4_Template, 1, 0, "ng-container", 11);
    i0.ɵɵconditionalCreate(5, UiFieldWrapperComponent_ng_template_0_Conditional_2_Conditional_5_Template, 2, 1, "span", 21);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    i0.ɵɵnextContext();
    const fieldTmpl_r6 = i0.ɵɵreference(1);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r0._useLabel() && !ctx_r0._usePlaceholder() ? 2 : -1);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngTemplateOutlet", fieldTmpl_r6);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!ctx_r0._useLabel() ? 5 : -1);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_1_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "label", 19);
    i0.ɵɵtext(1);
    i0.ɵɵtemplate(2, UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_1_ng_container_2_Template, 1, 0, "ng-container", 11);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    const helpButton_r5 = i0.ɵɵreference(4);
    i0.ɵɵproperty("title", ctx_r0._labelValue());
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0._labelValue(), "\u00A0 ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", helpButton_r5);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_3_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_3_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 21);
    i0.ɵɵtemplate(1, UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_3_ng_container_1_Template, 1, 0, "ng-container", 11);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵnextContext(3);
    const helpButton_r5 = i0.ɵɵreference(4);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", helpButton_r5);
  }
}
function UiFieldWrapperComponent_ng_template_0_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵconditionalCreate(1, UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_1_Template, 3, 3, "label", 19);
    i0.ɵɵtemplate(2, UiFieldWrapperComponent_ng_template_0_Conditional_3_ng_container_2_Template, 1, 0, "ng-container", 11);
    i0.ɵɵconditionalCreate(3, UiFieldWrapperComponent_ng_template_0_Conditional_3_Conditional_3_Template, 2, 1, "span", 21);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵnextContext();
    const fieldTmpl_r6 = i0.ɵɵreference(1);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0._useLabel() && !ctx_r0._usePlaceholder() ? 1 : -1);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", fieldTmpl_r6);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!ctx_r0._useLabel() ? 3 : -1);
  }
}
function UiFieldWrapperComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, UiFieldWrapperComponent_ng_template_0_ng_template_0_Template, 7, 9, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵconditionalCreate(2, UiFieldWrapperComponent_ng_template_0_Conditional_2_Template, 6, 3, "div", 4)(3, UiFieldWrapperComponent_ng_template_0_Conditional_3_Template, 4, 3, "div", 5);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(ctx_r0._useHorizontalLayout() ? 2 : 3);
  }
}
function UiFieldWrapperComponent_ng_template_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function UiFieldWrapperComponent_ng_template_3_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "ui-help-button", 22);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("htmlContent", ctx_r0.help());
  }
}
function UiFieldWrapperComponent_ng_template_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, UiFieldWrapperComponent_ng_template_3_Conditional_0_Template, 1, 1, "ui-help-button", 22);
    i0.ɵɵprojection(1, 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ctx_r0.help() ? 0 : -1);
  }
}
export let UiFieldWrapperComponent = /*#__PURE__*/(() => {
  class UiFieldWrapperComponent {
    constructor(cd,
    // public host used by inputRef to check for registration blocking
    host, injector) {
      this.cd = cd;
      this.host = host;
      this.injector = injector;
      this.destroyRef = inject(DestroyRef);
      this.label = input(null, ...(ngDevMode ? [{
        debugName: "label"
      }] : /* istanbul ignore next */[]));
      this.validations = input({}, ...(ngDevMode ? [{
        debugName: "validations"
      }] : /* istanbul ignore next */[]));
      this.description = input(...(ngDevMode ? [undefined, {
        debugName: "description"
      }] : /* istanbul ignore next */[]));
      this.help = input(...(ngDevMode ? [undefined, {
        debugName: "help"
      }] : /* istanbul ignore next */[]));
      this.placeholder = input(null, ...(ngDevMode ? [{
        debugName: "placeholder"
      }] : /* istanbul ignore next */[]));
      this.hidePlaceholder = input(false, ...(ngDevMode ? [{
        debugName: "hidePlaceholder"
      }] : /* istanbul ignore next */[]));
      this.noInput = input(false, ...(ngDevMode ? [{
        debugName: "noInput"
      }] : /* istanbul ignore next */[]));
      this.orientation = input('horizontal', ...(ngDevMode ? [{
        debugName: "orientation"
      }] : /* istanbul ignore next */[]));
      this.showResetButton = input(false, ...(ngDevMode ? [{
        debugName: "showResetButton"
      }] : /* istanbul ignore next */[]));
      this.resetValue = input(undefined, ...(ngDevMode ? [{
        debugName: "resetValue"
      }] : /* istanbul ignore next */[]));
      this.resetButtonClass = input('ui-button-invisible', ...(ngDevMode ? [{
        debugName: "resetButtonClass"
      }] : /* istanbul ignore next */[]));
      this._labelValue = computed(() => {
        const placeholder = this.placeholder();
        if (typeof placeholder === 'string') return placeholder;else return this.label();
      }, ...(ngDevMode ? [{
        debugName: "_labelValue"
      }] : /* istanbul ignore next */[]));
      this._usePlaceholder = computed(() => {
        return !!this.placeholder();
      }, ...(ngDevMode ? [{
        debugName: "_usePlaceholder"
      }] : /* istanbul ignore next */[]));
      this._useLabel = computed(() => {
        // empty string must be treated as assigned label, too!
        return !this._usePlaceholder() && this._labelValue() !== null;
      }, ...(ngDevMode ? [{
        debugName: "_useLabel"
      }] : /* istanbul ignore next */[]));
      this._useHorizontalLayout = computed(() => {
        const useHoriz = this._useLabel() && this.orientation() === 'horizontal';
        return useHoriz;
      }, ...(ngDevMode ? [{
        debugName: "_useHorizontalLayout"
      }] : /* istanbul ignore next */[]));
      this.reset = new EventEmitter();
      this.state = signal({
        errors: [],
        status: defaultFormControlStatus
      }, ...(ngDevMode ? [{
        debugName: "state"
      }] : /* istanbul ignore next */[]));
      // ContentChildren were working fine with regular inputRef directive.
      // But since inputRef is being used as a HostDirective, ContentChildren wont find it anymore.
      // FieldWrapper implementation is now passive and InputRef actively tries to find it and register itself.
      // Workaround for @ContentChild not finding anything... either due to nested loop, or because of several Directives with @if's
      // @ContentChildren(forwardRef(()=>InputRefDirective), {descendants:true}) 
      // inputs:any;
      // get input() {
      // 	return this.inputs.length ? this.inputs.first : null;
      // }
      this.input = signal(null, ...(ngDevMode ? [{
        debugName: "input"
      }] : /* istanbul ignore next */[]));
      this.hasValue = toSignal(toObservable(this.input).pipe(switchMap(i => {
        if (i) return i.hasValue$;else return of(false);
      })));
      this.hasAddonButtons = computed(() => {
        return !this.noInput() && (!this.state().status.enabled || this.showResetButton());
      }, ...(ngDevMode ? [{
        debugName: "hasAddonButtons"
      }] : /* istanbul ignore next */[]));
    }
    ngAfterViewInit() {
      if (this.noInput()) return;
      if (!IS_PRODUCTION) {
        if (!this.noInput()) {
          setTimeout(() => {
            if (!this.input()) {
              console.warn(`ui-field-wrapper (label: ${this._labelValue()}) is not setup up correctly. Make sure it contains an input with a InputRef directive or set [noInput]=true if it should explicitly be used without an input component.`, this);
              return;
            }
          }, 1500);
        }
      }
      // if(!IS_PRODUCTION && (this.input as any).formControl.name === 'password') {}
    }
    registerInput(inputRef) {
      if (this.noInput()) return;
      /**
       * special situation: some input components use other input components to compose their UI e.g. ui-input-checkboxes etc.
       * If they wrap any other component using InputRefDirective, this will result in this method being called by each of them.
       * Add InputRefBlockerDirective to those wrapped components - InputRefDirective will check for presence and not register itself then.
       */
      if (this.input()) {
        console.log('assigned InputRef:', this.input());
        console.log('incoming InputRef:', inputRef);
        console.log('FieldWrapper Component', this);
        throw new Error('FieldWrapper.registerInput was called multiple times. This may happen if using composed inputs. Make sure to "escape" nested inputs with inputRefBlocker.');
      }
      this.input.set(inputRef);
      // delay initial emission to fix ExpressionChangedAfterItHasBeenCheckedError 
      timer(10).pipe(switchMap(() => {
        return combineLatest({
          errors: inputRef.errors$,
          status: inputRef.controlStatus$
        });
      }), takeWhileAlive(this.destroyRef)).subscribe(state => {
        // if(state.errors) console.log('FieldWrapper error status:',this.input.formControl.name,state,this.input)
        this.state.set(state);
        this.cd.markForCheck();
      });
    }
    /**
     * NOTE: we need to support 2 ways to connect listener.
     * Due to dynamic rendering, field wrapper is unable to inject FieldWrapperListener easily.
     * For this reason, we search for listener inside of InputRef,
     * which has access to component and component providers using constructor injection.
     */
    registerListener(listener) {
      runInInjectionContext(this.injector, () => {
        const state$ = combineLatest({
          hasPlaceholder: combineLatest({
            value: toObservable(this._usePlaceholder),
            hidden: toObservable(this.hidePlaceholder)
          }).pipe(map(({
            value,
            hidden
          }) => !hidden && !!value)),
          hasLabel: toObservable(this._useLabel),
          orientation: toObservable(this._useHorizontalLayout).pipe(map(horiz => horiz ? 'horizontal' : 'vertical')),
          hasAddonButtons: toObservable(this.hasAddonButtons),
          hasHelp: toObservable(this.help).pipe(map(v => !!v)),
          hasDescription: toObservable(this.description).pipe(map(v => !!v))
        });
        state$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(state => {
          listener.onUpdateFieldWrapperState(state);
        });
      });
    }
    onClickReset() {
      this.reset.emit();
    }
    static {
      this.ɵfac = function UiFieldWrapperComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UiFieldWrapperComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Injector));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: UiFieldWrapperComponent,
        selectors: [["ui-field-wrapper"]],
        inputs: {
          label: [1, "label"],
          validations: [1, "validations"],
          description: [1, "description"],
          help: [1, "help"],
          placeholder: [1, "placeholder"],
          hidePlaceholder: [1, "hidePlaceholder"],
          noInput: [1, "noInput"],
          orientation: [1, "orientation"],
          showResetButton: [1, "showResetButton"],
          resetValue: [1, "resetValue"],
          resetButtonClass: [1, "resetButtonClass"]
        },
        outputs: {
          reset: "reset"
        },
        ngContentSelectors: _c1,
        decls: 5,
        vars: 1,
        consts: [["inputTmpl", ""], ["helpButton", ""], ["fieldTmpl", ""], [3, "ngLet"], [1, "field-wrapper", "horiz"], [1, "field-wrapper", "vert"], [1, "", 3, "ngClass"], [1, "control-label"], [1, "input-wrapper"], [3, "stretchInputs"], [1, "description", 3, "innerHTML"], [4, "ngTemplateOutlet"], ["uiInputGroupAddon", "", 1, "input-addon-locked"], ["uiInputGroupAddon", "", "uiButton", "", "icon", "sync-alt", "title", "Zur\u00FCcksetzen", 3, "class"], ["icon", "lock"], ["uiInputGroupAddon", "", "uiButton", "", "icon", "sync-alt", "title", "Zur\u00FCcksetzen", 3, "click"], [1, "error"], [1, "text-danger"], [1, "fw-left"], [1, "control-label", 3, "title"], [1, "fw-right"], [1, "overlay-help-button"], [3, "htmlContent"]],
        template: function UiFieldWrapperComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵtemplate(0, UiFieldWrapperComponent_ng_template_0_Template, 4, 1, "ng-template", 3)(1, UiFieldWrapperComponent_ng_template_1_Template, 1, 0, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(3, UiFieldWrapperComponent_ng_template_3_Template, 2, 1, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
          }
          if (rf & 2) {
            i0.ɵɵproperty("ngLet", ctx.state());
          }
        },
        dependencies: [CommonModule, i1.NgClass, i1.NgTemplateOutlet, FormsModule, UiInputGroupComponent, UiInputGroupAddonDirective, UiIconsModule, i2.UiIconComponent, UiHelpButtonComponent, NgLetDirective, UiButtonComponent, SafeHtmlPipe],
        styles: ["[_nghost-%COMP%]{display:block}  ui-field-wrapper+ui-field-wrapper{margin-top:8px}[_nghost-%COMP%] > .field-wrapper[_ngcontent-%COMP%]{position:relative}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > label[_ngcontent-%COMP%]{display:block;margin-bottom:.5em}@media (min-width: 420px){[_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%]{display:flex;align-items:center}[_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-left[_ngcontent-%COMP%]{align-self:flex-start}[_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-left[_ngcontent-%COMP%]{width:30%;min-width:130px;max-width:300px}[_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%]{flex:1}[_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-left[_ngcontent-%COMP%] > label[_ngcontent-%COMP%]{display:block;margin-top:7px}}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%], [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]{position:relative;color:var(--inputTextColor)}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%] > .control-label[_ngcontent-%COMP%], [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%] > .control-label[_ngcontent-%COMP%]{position:absolute;top:.8em;left:10px;transition:top .2s,left .2s,font-size .2s;pointer-events:none;z-index:9;opacity:.65}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label.placeholder-small[_ngcontent-%COMP%] > .control-label[_ngcontent-%COMP%], [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label.placeholder-small[_ngcontent-%COMP%] > .control-label[_ngcontent-%COMP%]{top:.3em;font-size:.75em;left:10px}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     textarea, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-dropdown .p-dropdown-label, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-input, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     input, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     textarea, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-dropdown .p-dropdown-label, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-input, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     input{padding-top:var(--inputWithPlaceholderPaddingTop)}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     textarea, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-dropdown, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-input, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     input, [_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-input-group, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     textarea, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-dropdown, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-input, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     input, [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%]:not(.placeholder-hide)     .p-input-group{--inputGroupElementHeight:var(--inputWithPlaceholderHeight);--inputHeight:var(--inputWithPlaceholderHeight)}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label.placeholder-hide[_ngcontent-%COMP%] > .control-label[_ngcontent-%COMP%], [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label.placeholder-hide[_ngcontent-%COMP%] > .control-label[_ngcontent-%COMP%]{top:.35em;opacity:.5}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label.placeholder-hide.placeholder-small[_ngcontent-%COMP%]   label[_ngcontent-%COMP%], [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label.placeholder-hide.placeholder-small[_ngcontent-%COMP%]   label[_ngcontent-%COMP%]{display:none}[_nghost-%COMP%] > .field-wrapper.vert[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%] + .overlay-help-button[_ngcontent-%COMP%], [_nghost-%COMP%] > .field-wrapper.horiz[_ngcontent-%COMP%] > .fw-right[_ngcontent-%COMP%] > .placeholder-label[_ngcontent-%COMP%] + .overlay-help-button[_ngcontent-%COMP%]{position:absolute;top:12px;right:-36px}.input-addon-locked[_ngcontent-%COMP%]{background-color:var(--disabledBgColor);color:var(--textColor)}.error[_ngcontent-%COMP%]{background:var(--validationErrorBgColor);color:var(--validationErrorTextColor);border-radius:var(--borderRadius);padding:0 10px;font-size:.8em;letter-spacing:.03em;margin-top:1px}.description[_ngcontent-%COMP%]{margin:6px 0 10px;font-size:var(--fontSizeSmall);color:var(--textSoftColor)}label[_ngcontent-%COMP%]    >ui-help-button{display:inline-block;margin-left:10px}.input-wrapper[_ngcontent-%COMP%]{display:flex}.input-wrapper[_ngcontent-%COMP%]    >:first-child{flex:1}.input-wrapper[_ngcontent-%COMP%]    >*+*{margin-left:8px}"],
        changeDetection: 0
      });
    }
  }
  return UiFieldWrapperComponent;
})();